import { Skeleton } from '@chakra-ui/react';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { FundraisingType, routes } from '@blockpulse3/data/shared';
import { useCreateFundraisingMutation, useGetCompanyQuery } from '@blockpulse3/graphql/hooks';

import { crowdfundingFundraisingParametersDefaults } from '../../../utils';
import { ICrowdfundingFundraisingParametersForm } from '../types';
import { NewCrowdfundingFundraisingInformations } from './NewCrowdfundingFundraisingInformations';

type Props = unknown;

/**
 * NewCrowdfundingFundraisingCreate.
 * First step of private fundraising funnel.
 *
 * @returns {JSX.Element}
 */
export function NewCrowdfundingFundraisingCreate(): JSX.Element {
  const navigate = useNavigate();

  const { companyId = '' } = useParams();
  const companyReq = useGetCompanyQuery({
    variables: { companyId },
    skip: !companyId,
  });
  const company = companyReq?.data?.company;

  const isWorkspaceLoaded = !!company;

  const [createFundraising, { loading }] = useCreateFundraisingMutation();

  const defaultValues = useMemo(() => {
    return company && crowdfundingFundraisingParametersDefaults(company);
  }, [company]);

  const handleFormSubmit = (data: ICrowdfundingFundraisingParametersForm): void => {
    if (company) {
      const selectedHoldingMethods = data.allowedHoldingMethods.map(
        (holdingMethod) => holdingMethod.value,
      );

      createFundraising({
        variables: {
          createFundraisingInput: {
            name: data.name,
            companyId,
            pricePerShare: data.pricePerShare,
            subscriptionPeriod: data.subscriptionPeriod.value,
            assetType: data.assetType,
            allowedHoldingMethods: selectedHoldingMethods,
            type: FundraisingType.CROWDFUNDING,
            hardCap: data.hardCap,
            subscriptionWorkflowType: data.subscriptionWorkflowType,
            minimalAmount: data.minimalAmount,
            maximalAmount: data.maximalAmount,
            hasMifid: data.hasMifid,
            softCap: data.softCap,
          },
        },
        onCompleted: (data) => {
          if (company) {
            /* ** Remove /create from history when we go back ** */
            window.history.replaceState(
              null,
              '',
              routes.workspace.fundraising.crowdfunding.parameters.fmt(
                companyId,
                data.createFundraising.id,
              ),
            );
            navigate(
              routes.workspace.fundraising.crowdfunding.documentation.fmt(
                companyId,
                data.createFundraising.id,
              ),
            );
          }
        },
      });
    }
  };

  return (
    <Skeleton isLoaded={isWorkspaceLoaded} w="full">
      <NewCrowdfundingFundraisingInformations
        defaultValues={defaultValues}
        isLoading={loading}
        onSubmit={handleFormSubmit}
      />
    </Skeleton>
  );
}

export type NewCrowdfundingFundraisingCreateProps = Props;
